<template>
  <button class="game-preview-card" @click="$emit('expand', game)">
    <div class="cover-container">
      <img
        v-if="game.image"
        :src="formatUrl(game.image)"
        alt="Game Cover"
        class="game-cover"
        @error="handleImageError"
      />
      <div v-else class="cover-placeholder">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="placeholder-icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
        </svg>
      </div>
    </div>

    <div class="game-info">
      <h3 class="game-title">{{ game.name }}</h3>

      <p v-if="shortDescription" class="game-description">
        {{ shortDescription }}
      </p>

      <div class="meta-row">
        <span v-if="game.release_date" class="meta-pill">
          {{ formatYear(game.release_date) }}
        </span>
        <span v-if="game.metacritic_score > 0" class="meta-pill score">
          ★ {{ game.metacritic_score }}
        </span>
        <span v-if="reviewRatio" class="meta-pill">
          {{ reviewRatio }}
        </span>
        <span v-if="displayPriceText" class="meta-pill price">
          {{ displayPriceText }}
        </span>
      </div>
    </div>

    <div v-if="previewScreenshot" class="screenshot-container">
      <img
        :src="formatUrl(previewScreenshot)"
        alt="Game Screenshot"
        class="preview-screenshot"
        @error="hideScreenshot"
      />
    </div>
  </button>
</template>

<script>
import { secureUrl } from '@/utils/secure-url.js';

export default {
  name: 'GamePreviewCard',
  props: {
    game: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      screenshotVisible: true
    };
  },
  computed: {
    shortDescription() {
      const raw = this.game.short_description || this.game.description || '';
      const text = raw.replace(/<[^>]*>/g, '').trim();
      if (!text) return '';
      return text.length > 110 ? `${text.slice(0, 107)}...` : text;
    },
    reviewRatio() {
      if (
        this.game.positive_reviews != null &&
        this.game.total_reviews != null &&
        this.game.total_reviews > 0
      ) {
        const percent = Math.round((this.game.positive_reviews / this.game.total_reviews) * 100);
        return `${percent}% positive`;
      }
      return '';
    },
    primaryPrice() {
      return this.game.prices?.[0] || this.game.price || null;
    },
    displayPriceText() {
      if (!this.primaryPrice) return '';
      if (typeof this.primaryPrice === 'string') return this.primaryPrice;
      if (this.primaryPrice.final_formatted) return this.primaryPrice.final_formatted;
      if (this.primaryPrice.formatted) return this.primaryPrice.formatted;
      return '';
    },
    previewScreenshot() {
      if (!this.screenshotVisible) return '';
      if (Array.isArray(this.game.screenshots) && this.game.screenshots.length) {
        const first = this.game.screenshots[0];
        if (typeof first === 'string') return first;
        return first.url || first.path || first.image || '';
      }
      return this.game.screenshot || '';
    }
  },
  methods: {
    formatUrl(url) {
      return secureUrl(url);
    },
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    hideScreenshot() {
      this.screenshotVisible = false;
    },
    formatYear(date) {
      const d = new Date(date);
      return Number.isNaN(d.getTime()) ? date : d.getFullYear();
    }
  }
};
</script>

<style scoped>
.game-preview-card {
  width: 100%;
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) 120px;
  gap: 14px;
  align-items: center;
  background: #18181b;
  border: 1px solid #27272a;
  border-radius: 6px;
  padding: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
}

.game-preview-card:hover {
  border-color: #00dc82;
  background: #222226;
  transform: translateY(-1px);
}

.cover-container {
  width: 72px;
  height: 72px;
  border-radius: 4px;
  overflow: hidden;
  background: #27272a;
  flex-shrink: 0;
}

.game-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #52525b;
}

.placeholder-icon {
  width: 24px;
  height: 24px;
}

.game-info {
  min-width: 0;
}

.game-title {
  margin: 0 0 6px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.game-description {
  margin: 0 0 8px;
  color: #a1a1aa;
  font-size: 0.9rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-pill {
  font-size: 0.78rem;
  color: #d4d4d8;
  background: #27272a;
  border: 1px solid #3f3f46;
  border-radius: 999px;
  padding: 4px 8px;
}

.meta-pill.price {
  color: #00dc82;
  border-color: rgba(0, 220, 130, 0.35);
}

.screenshot-container {
  width: 120px;
  height: 68px;
  border-radius: 4px;
  overflow: hidden;
  background: #27272a;
  border: 1px solid #3f3f46;
}

.preview-screenshot {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 900px) {
  .game-preview-card {
    grid-template-columns: 72px minmax(0, 1fr);
  }

  .screenshot-container {
    display: none;
  }
}
</style>